<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import { ItpLoadingIndicator, ItpLoadError } from '../loading';
import { useKeepAliveScroll } from './useKeepAliveScroll';

const props = withDefaults(
  defineProps<{
    loading?: boolean;
    reloading?: boolean;
    loadingSpinner?: boolean;
    loaded?: boolean;
    error?: Error | string | null;
    modal?: boolean;
    card?: boolean;
    modalBindings?: Record<string, any>;
    size?: string;
    modalName?: string;
    header?: string;
    noShow?: boolean;
  }>(),
  {
    loading: false,
    reloading: false,
    loadingSpinner: false,
    loaded: false,
    error: null,
    modal: false,
    card: false,
    modalBindings: () => ({}),
    size: undefined,
    modalName: undefined,
    header: undefined,
    noShow: false,
  },
);

const emit = defineEmits<{
  (e: 'close', args?: any): void;
  (e: 'reload'): void;
}>();

const pageRoot = useTemplateRef<HTMLElement>('pageRoot');
useKeepAliveScroll(pageRoot);

const showError = computed(() => !!props.error && !props.loaded);

const componentName = computed(() => (props.modal ? 'ItpModal' : 'div'));

const componentBindings = computed(() => {
  if (!props.modal) {
    return { class: 'h-100' };
  }
  let result: Record<string, any> = { ...props.modalBindings };
  if (props.size) {
    result.size = props.size;
  }
  if (props.header) {
    result.header = props.header;
  }
  if (props.noShow !== undefined) {
    result.noShow = props.noShow;
  }

  result.loading = !props.loaded && !showError.value;
  return result;
});

function onClose(args: any) {
  emit('close', args);
}

function onReload() {
  emit('reload');
}
</script>

<template>
  <component :is="componentName" v-bind="componentBindings" @close="onClose">
    <template v-if="modal" #header>
      <slot name="header" />
    </template>

    <template v-if="modal" #headerCenter>
      <slot name="headerCenter" />
    </template>

    <slot v-if="modal && !showError" />

    <ItpLoadError
      v-if="modal && showError"
      :error="error"
      :reloading="reloading"
      @reload="onReload"
    />

    <template v-if="modal" #footer>
      <slot name="footer" />
    </template>

    <div v-if="!modal && !card" ref="pageRoot" class="page--v2">
      <slot v-if="loaded" name="header" />
      <ItpLoadingIndicator v-if="(!loaded || loading) && !showError" fill />
      <ItpLoadError
        v-if="showError"
        class="flex-grow-1"
        style="min-height: 0"
        :error="error"
        :reloading="reloading"
        @reload="onReload"
      />
      <div
        v-if="loaded && !loading"
        class="overflow-hidden d-flex flex-column"
        style="flex: 1; min-height: 0"
      >
        <slot />
      </div>
      <slot v-if="loaded" name="footer" />
    </div>

    <div
      v-if="!modal && card"
      ref="pageRoot"
      class="page--v2 overflow-auto bg-page"
    >
      <div class="container py-4 h-100">
        <div class="card shadow-sm h-100">
          <slot v-if="loaded" name="header" />
          <ItpLoadingIndicator v-if="(!loaded || loading) && !showError" fill />
          <ItpLoadError
            v-if="showError"
            class="flex-grow-1"
            style="min-height: 0"
            :error="error"
            :reloading="reloading"
            @reload="onReload"
          />
          <div
            v-if="loaded && !loading"
            class="overflow-hidden d-flex flex-column"
            style="flex: 1; min-height: 0"
          >
            <slot />
          </div>
          <slot v-if="loaded" name="footer" />
        </div>
      </div>
    </div>
  </component>
</template>
